import { useState } from "react";
import { toast } from "sonner";
import { Wallet as WalletIcon, RefreshCw, Unlink, CheckCircle2, XCircle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useGame } from "@/context/GameContext";
import { boostList } from "@/components/BoostBadges";
import MwpSection from "@/components/MwpSection";
import HoldingsGuide from "@/components/HoldingsGuide";
import { BuyLink } from "@/components/BuyLink";
import MyPack from "@/components/MyPack";
import SolanaLink from "@/components/SolanaLink";

const toHex = (s) => "0x" + Array.from(new TextEncoder().encode(s)).map((b) => b.toString(16).padStart(2, "0")).join("");

export default function Wallet() {
  const { game, refresh } = useGame();
  const [busy, setBusy] = useState(false);
  const w = game.wallet;
  const list = boostList(game.boosts, game.settings);
  const colls = Object.fromEntries(game.settings.collections.map((c) => [c.key, c]));

  const connect = async () => {
    if (!window.ethereum) return toast.error("No browser wallet found. Install MetaMask, then reload this page.");
    setBusy(true);
    try {
      const [address] = await window.ethereum.request({ method: "eth_requestAccounts" });
      const { data } = await api.post("/wallet/nonce", { address });
      const signature = await window.ethereum.request({ method: "personal_sign", params: [toHex(data.message), address] });
      await api.post("/wallet/verify", { nonce: data.nonce, signature });
      const g = await refresh();
      const n = ["c1", "c2", "c3"].filter((k) => g?.boosts?.[k]).length;
      toast.success(n ? `Wallet linked. ${n} boost${n > 1 ? "s" : ""} unlocked!` : "Wallet linked. No collection NFTs found in it yet.");
    } catch (e) {
      toast.error(e?.code === 4001 ? "Signature cancelled" : errMsg(e));
    } finally { setBusy(false); }
  };
  const recheck = async () => { setBusy(true); try { await api.post("/wallet/refresh"); await refresh(); toast.success("Holdings re-checked"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const unlink = async () => { await api.post("/wallet/unlink"); await refresh(); toast("Wallet unlinked. Boosts are off."); };

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
      <p className="text-xs uppercase tracking-[0.3em] font-mono-game text-amber-400/80 mb-3">Polygon &amp; Solana · Read-only check</p>
      <h1 className="font-display text-4xl sm:text-5xl text-amber-100 mb-4">NFT Boosts</h1>
      <p className="text-slate-400 max-w-2xl mb-10">Sign a free message to prove the wallet is yours. It costs no gas, sends no transaction and never touches your NFTs. Boosts switch on for each collection you hold, and you can play as any wolf or PUP you own. Ownership is re-checked every time you sign in.</p>

      <section className="glass rounded-2xl p-6 mb-8 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="wallet-status">
        <WalletIcon className="w-8 h-8 text-sky-300 shrink-0" />
        {w ? (
          <>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-mono-game text-slate-400">LINKED WALLET</p>
              <p data-testid="wallet-address" className="font-mono-game text-sky-200 truncate">{w.address}</p>
              <p className="text-[11px] text-slate-500 mt-1">Last checked {new Date(w.checked_at).toLocaleString()}</p>
            </div>
            <button data-testid="wallet-refresh-btn" disabled={busy} onClick={recheck} className="flex items-center gap-2 px-4 py-2 rounded-full border border-slate-600 text-sm hover:border-sky-400"><RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />Re-check</button>
            <button data-testid="wallet-unlink-btn" onClick={unlink} className="flex items-center gap-2 px-4 py-2 rounded-full text-sm text-slate-400 hover:text-red-300"><Unlink className="w-4 h-4" />Unlink</button>
          </>
        ) : (
          <>
            <p className="flex-1 text-slate-300">No wallet linked. Each wallet can be linked to one game account.</p>
            <button data-testid="wallet-connect-btn" disabled={busy} onClick={connect} className="ember-btn px-6 py-3 rounded-full text-sm">{busy ? "WAITING FOR WALLET…" : "CONNECT METAMASK"}</button>
          </>
        )}
      </section>

      <MwpSection wallet={w} />

      <SolanaLink game={game} refresh={refresh} />

      <section className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {list.map((x) => (
          <div key={x.key} data-testid={`boost-card-${x.key}`} data-active={x.on ? "true" : "false"}
            className={`rounded-2xl p-6 border transition-all ${x.on ? "nft-glow bg-gradient-to-b from-amber-950/50 to-slate-950" : "border-slate-800 bg-slate-950/60"}`}>
            <div className="flex items-center justify-between mb-6">
              <span className="flex items-center gap-3">
                <img src={game.badge_art?.[x.key] || x.sample} alt="" loading="lazy" className={`w-14 h-14 rounded-xl object-cover border ${x.on ? "border-amber-400/70" : "border-slate-700 grayscale-[.6] opacity-70"}`} />
                <x.icon className={`w-6 h-6 ${x.color}`} />
              </span>
              {x.on ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <XCircle className="w-5 h-5 text-slate-600" />}
            </div>
            <p className="text-[11px] font-mono-game text-slate-500">{x.key === "pack" ? "HOLD ALL THREE" : x.key === "sol" ? "SOLANA · GROWS PER WOLF" : `COLLECTION ${x.key.slice(1)}`}</p>
            <h3 className="font-display text-lg text-slate-100 mb-3">{x.name}</h3>
            <p className={`text-sm ${x.on ? x.color : "text-slate-400"}`}>{x.text}</p>
            {colls[x.key] && <p className="text-[10px] font-mono-game text-slate-600 mt-4 break-all">{colls[x.key].address}</p>}
            {x.key === "sol" && game.sol_wallet && <p className="text-xs text-slate-400 mt-2" data-testid="boost-count-sol">Held: {game.sol_wallet.count}</p>}
            {w && colls[x.key] && <p className="text-xs text-slate-400 mt-2" data-testid={`boost-count-${x.key}`}>Held: {w.counts?.[x.key] ?? 0}</p>}
            <BuyLink k={x.key} testid={`boost-buy-${x.key}`} className="mt-4" />
          </div>
        ))}
      </section>
      {w && <MyPack game={game} refresh={refresh} />}
      <HoldingsGuide game={game} />
    </main>
  );
}
